import React, { useRef } from 'react'

import { makeStyles } from '@mui/styles'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import { user } from 'ampersand-app' // FLAG:useDocuAi — read the per-tenant experimental flag
import { SORT_ORDER } from '@/enums/pagination'
import SelectInput from '@/modules/base/components/inputs/select-input/select-input'
import Search from '@/modules/base/components/list/search/search'
import clsx from 'clsx'
import lang from '@/lang'
import { CollaborationSelect } from '@/modules/todo/components/todo-form/collaboration-select'
import store, { dispatch, useAppSelector } from '@/redux/store'
import ListQuickFilter from '@/modules/base/components/list/filter/list-quick-filter'
import { cancelUploads } from '@/redux/ducks/document/upload'
import { terminateWorker } from '@/redux/ducks/worker'
import { useOpenModal } from '@/modules/_helpers/openModal'
import { useSearchParams } from 'react-router'
import { useDmsCollaborationToken } from '@/modules/document/_helpers/useDmsCollaborationToken'
import { SquareIconButton, ButtonSquare, CancelButton } from '@/modules/base/components/buttons'
import { Title } from '@/modules/base/components/texts'
import { TextareaInput } from '@/modules/base/components/inputs'
import FileService from '@/modules/file/fileService'
import { toast } from '@/modules/base/components/toast/toastConfig'
import { experimentalFeatures } from '@/modules/base/util/constants'
import ThemeInterface from '@/theme-interface'
import { Folder } from '@/modules/file/components/folder-nav/types'

const GAPS = '14px'
const COMPACT_GAP = '12px'

export interface FileSortOrder {
  field: string,
  value: string,
}

interface FileToolbarProps {
  onSearchChanged?: (value: string) => void,
  onSortChange?: (sortOrder: FileSortOrder) => void,
  className?: string,
  hideCollaborationSelect?: boolean,
  showQuickFilter?: boolean,
  children?: React.ReactNode,
  currentFolder?: Folder | null,
  resetFilter?: () => void,
  compactSpacing?: boolean,
}

const useStyles = makeStyles((theme: ThemeInterface) => ({
  container: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  controls: {
    display: 'flex',
  },
  right: {
    display: 'flex',
  },
  search: {
    width: 290,
    height: 45,
    marginBottom: 6,
  },
  searchReport: {
    marginLeft: 6,
    marginBottom: 6,
  },
  modalWrapper: {
    padding: 22,
  },
  modalTitle: {
    textAlign: 'left',
    marginBottom: 12,
  },
  modalButton: {
    width: 145,
  },
  sortFilter: {
    width: 242,
    marginLeft: GAPS,
    '& > div > div > div': {
      backgroundColor: '#fff',
    },
  },
  compactSortFilter: {
    marginLeft: COMPACT_GAP,
  },
  collaborationSelect: {
    marginRight: GAPS,
    width: 242,
    '& .react-select__control': {
      background: theme.white,
      '& .react-select__indicators': {
        height: '100%',
      },
      '& .react-select__value-container': {
        height: '100%',
        display: 'flex',
        '&> div': {
          position: 'absolute',
        },
      },
    },
    '& .select-input__selectContainer': {
      width: '100%',
      '& > div': {
        width: '100%',
      },
    },
  },
  compactCollaborationSelect: {
    marginRight: COMPACT_GAP,
  },
}))

export const FileToolbar = (props: FileToolbarProps) => {
  const {
    onSearchChanged,
    onSortChange,
    className,
    hideCollaborationSelect = true,
    showQuickFilter = true,
    children,
    currentFolder = null,
    resetFilter,
    compactSpacing = false,
  } = props

  const [searchParams] = useSearchParams()
  const openModal = useOpenModal()

  const selectedItems = useAppSelector(state => state.document.list.selectedItems)
  const pending = useAppSelector(state => state.document.upload.pending)
  const calculating = useAppSelector(state => state.document.upload.calculating)

  const setCollaborationToken = useDmsCollaborationToken()

  const performTenantSwitch = (collaborationId) => {
    dispatch(cancelUploads())
    dispatch(terminateWorker())
    setCollaborationToken(collaborationId === 'internal' ? null : collaborationId, resetFilter)
  }

  const onCollaborationChanged = ({ value: collaborationId }) => {
    const hasUploads = pending.filter(({ running, error }) => !!running && !error).length || calculating.filter(({ error }) => !error).length
    if (hasUploads) {
      openModal(() => ({
        component: 'ConfirmModal',
        props: {
          onConfirm: () => performTenantSwitch(collaborationId),
          title: lang.documents.upload.interruptPendingUploadsTitle,
          description: lang.documents.upload.interruptPendingUploadsDescription,
        },
      }))
    } else {
      performTenantSwitch(collaborationId)
    }
  }

  const { subscriptions } = store.getState().tenant.info
  const isTaxConsultant = subscriptions?.some(s => s.isTaxConsultant)

  const sortOptions = [
    { label: lang.documents.orderByRecordDateDesc, value: { field: 'recordDate', value: SORT_ORDER.DESC } },
    { label: lang.documents.orderByRecordDateAsc, value: { field: 'recordDate', value: SORT_ORDER.ASC } },
    { label: lang.documents.orderByCreatedAtDesc, value: { field: 'addedAt', value: SORT_ORDER.DESC } },
    { label: lang.documents.orderByCreatedAtAsc, value: { field: 'addedAt', value: SORT_ORDER.ASC } },
    { label: lang.documents.orderByName, value: { field: 'searchName', value: SORT_ORDER.ASC } },
    { label: lang.documents.favoritesFirst, value: { field: 'isFavorite', value: SORT_ORDER.DESC } },
  ]

  if (selectedItems?.length) {
    sortOptions.push({ label: lang.documents.selectedFirst, value: { field: 'selected', value: SORT_ORDER.DESC } })
  }

  const classes = useStyles()

  const handleSearchInputChanged = (value) => {
    onSearchChanged?.(value)
  }

  // FLAG:docuAiEnhancements — "Such-Problem melden": the search is served by the DMS
  // searchapi, so a problem with it is a DMS-targeted issue (not DocuAI). The icon
  // only shows once a search is active (a searchTerm is present). Posts straight to
  // the DMS public API with target='dms' + the current searchTerm (no document).
  // Rides the child flag: reports are internal-only for now (PO holdback).
  const docuAiEnhancementsEnabled = user?.experimentalFeatures?.includes(experimentalFeatures.docuAiEnhancements)
  const activeSearchTerm = (searchParams.get('searchTerm') ?? '').trim()
  const reportTextRef = useRef('')

  const onSearchReportSubmit = (close) => {
    const description = reportTextRef.current.trim()
    if (!description) return
    FileService.reportProblem({ target: 'dms', searchTerm: activeSearchTerm, description })
      .then(() => {
        toast.showToast({ type: 'success', message: lang.documents.drawer.footer.reportProblemSuccess })
        close()
      })
      .catch((error) => {
        // Network failure / 429 anti-spam cap / generic — the dialog stays open with
        // the text preserved, so the user can retry (FileService maps the message).
        toast.showToast({ type: 'error', message: FileService.getReportProblemErrorMessage(error) })
      })
  }

  const handleReportSearchProblem = () => {
    reportTextRef.current = ''
    openModal(close => ({
      component: 'ModalSmall',
      children: <div className={classes.modalWrapper}>
        <Title className={classes.modalTitle} position='center'>{lang.documents.drawer.footer.reportSearchProblemTitle}</Title>
        <TextareaInput
          useFormik={false}
          enableAiFeatures={false}
          name='reportSearchProblem'
          rows={5}
          placeholder={lang.documents.drawer.footer.reportSearchProblemPlaceholder}
          onChange={(value) => { reportTextRef.current = value }}
        />
      </div>,
      props: {
        onBackdropClick: close,
        showCloseIcon: false,
        actions: [
          <CancelButton type='cancel' label={lang.cancelButtonLabel} onClick={close} className={classes.modalButton} dataQsId='reportSearchProblem-cancel' />,
          <ButtonSquare
            className={classes.modalButton}
            onClick={() => { onSearchReportSubmit(close) }}
            label={lang.documents.drawer.footer.reportProblemSubmit}
            dataQsId='reportSearchProblem-submit'
          />,
        ],
      },
    }))
  }

  const handleSortChange = (optionRef) => {
    onSortChange?.(optionRef.value)
  }

  const collaborationSelect = (isTaxConsultant) && !hideCollaborationSelect && (
    <CollaborationSelect
      useDefault
      className={clsx(classes.collaborationSelect, compactSpacing && classes.compactCollaborationSelect)}
      onChange={onCollaborationChanged} />
  )

  return (
    <div className={classes.container}>
      <div className={clsx(classes.controls, className)}>
        {collaborationSelect}
        <Search
          placeholder={currentFolder?.name ? `${currentFolder.name} durchsuchen` : lang.documents.searchPlaceholder}
          customClass={classes.search}
          initValue={searchParams.get('searchTerm')}
          inputChanged={handleSearchInputChanged}
        />
        {docuAiEnhancementsEnabled && activeSearchTerm && <SquareIconButton
          className={classes.searchReport}
          tooltip={lang.documents.drawer.footer.reportSearchProblem}
          onClick={handleReportSearchProblem}
          icon={ReportProblemOutlinedIcon}
          size='large'
          variant={'white'} />}
        <div className={classes.right}>
          <SelectInput
            name="fileSortOrder"
            handleChange={handleSortChange}
            containerClass={clsx(classes.sortFilter, compactSpacing && classes.compactSortFilter)}
            notAsync
            allowCreate={false}
            customSelectOptions={{ defaultValue: sortOptions[2] }}
            options={sortOptions}
          />
        </div>
      </div>
      {showQuickFilter ? <ListQuickFilter listSettingName={'documents'} /> : null}
      {children}
    </div>
  )
}

export default FileToolbar
